import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import OrderManager from "../common/OrderManager";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../constants";
import { getReadableDate } from "../../utils";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import DialogAddStudentToBatch from "./DialogAddStudentToBatch";
import DialogAddBatchController from "./DialogAddBatchController";
import DialogBulkGlobalNote from "./DialogBulkGlobalNote";
import DialogBulkCounselingNote from "./DialogBulkCounselingNote";
import BatchStudent from "./BatchStudent";
import BatchScheduleEditor from "./BatchScheduleEditor";

export default function BatchDetail() {
    const { batchId } = useParams();
    const navigate = useNavigate();
    const { requestAPI, showToast } = useAppContext();

    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [batch, setBatch] = useState();
    const [activeTab, setActiveTab] = useState(0);

    const [studentsLoading, setStudentsLoading] = useState();
    const [studentsError, setStudentsError] = useState();
    const [students, setStudents] = useState();
    const [studentSearch, setStudentSearch] = useState("");
    const [updatingViewIndex, setUpdatingViewIndex] = useState();
    const [savingRollNos, setSavingRollNos] = useState();

    const [dialogAddStudent, setDialogAddStudent] = useState({ visible: false });
    const [dialogAddController, setDialogAddController] = useState({ visible: false });
    const [dialogBulkGlobalNote, setDialogBulkGlobalNote] = useState({ visible: false });
    const [dialogBulkCounselingNote, setDialogBulkCounselingNote] = useState({ visible: false });

    const closeDialogAddStudent = useCallback(() => {
        setDialogAddStudent((prev) => ({ ...prev, visible: false }));
    }, []);

    const closeDialogAddController = useCallback(() => {
        setDialogAddController((prev) => ({ ...prev, visible: false }));
    }, []);

    const closeDialogBulkGlobalNote = useCallback(() => {
        setDialogBulkGlobalNote((prev) => ({ ...prev, visible: false }));
    }, []);

    const closeDialogBulkCounselingNote = useCallback(() => {
        setDialogBulkCounselingNote((prev) => ({ ...prev, visible: false }));
    }, []);

    useEffect(() => {
        requestAPI({
            requestPath: `batches/${batchId}`,
            requestMethod: "GET",
            setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setBatch(data);
                } else {
                    setError("Couldn't load Batch");
                }
            },
        });
    }, [batchId, requestAPI]);

    useEffect(() => {
        requestAPI({
            requestPath: `batches/${batchId}/users`,
            requestMethod: "GET",
            setLoading: setStudentsLoading,
            onRequestFailure: setStudentsError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setStudents(data);
                } else {
                    setStudentsError("Couldn't load Students");
                }
            },
        });
    }, [batchId, requestAPI]);

    const updateRollNos = useCallback(() => {
        requestAPI({
            requestPath: `batches/${batchId}/users/roll_nos`,
            requestMethod: "PATCH",
            requestPostBody: (students || []).map(({ id }, index) => ({ id, roll_no: index + 1 })),
            setLoading: setSavingRollNos,
            parseResponseBody: false,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Roll Numbers !", life: 2000 }),
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 200) {
                    setStudents((prev) => (prev || []).map((student, index) => ({ ...student, roll_no: index + 1 })));
                    showToast({ severity: "success", summary: "Updated", detail: "Roll Numbers Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Roll Numbers !", life: 2000 });
                }
            },
        });
    }, [batchId, requestAPI, showToast, students]);

    const studentCount = students?.length || 0;

    const filteredStudents = useMemo(() => {
        if (updatingViewIndex) {
            return students;
        }

        const query = studentSearch.trim().toLowerCase();
        if (!query) {
            return students;
        }

        return (students || []).filter((student) => {
            const name = (student.full_name || "").toLowerCase();
            const roll = String(student.roll_no ?? "").toLowerCase();
            const userId = String(student.user_id ?? "").toLowerCase();
            const email = (student.email || "").toLowerCase();
            const phone = (student.phone || "").toLowerCase();
            return name.includes(query) || roll.includes(query) || userId.includes(query) || email.includes(query) || phone.includes(query);
        });
    }, [studentSearch, students, updatingViewIndex]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title={batch?.title || "Batch"} />
            <TabHeader
                className="px-3 pt-3"
                title={batch?.title || "Batch Details"}
                highlights={[
                    batch?.active ? "Active" : "Inactive",
                    batch?.branch_title || "No Branch",
                    `${studentCount} Student${studentCount === 1 ? "" : "s"}`,
                ]}
                actionItems={[
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.ASSIGN_BATCH_STUDENT}>
                        <Button
                            icon="pi pi-user-plus"
                            severity="warning"
                            disabled={updatingViewIndex || savingRollNos}
                            onClick={() =>
                                setDialogAddStudent({
                                    visible: true,
                                    batchId,
                                    students,
                                    setStudents,
                                    closeDialog: closeDialogAddStudent,
                                })
                            }
                        />
                    </HasRequiredAuthority>,
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.MANAGE_BATCH_ATTENDANCE}>
                        <Button icon="pi pi-check-square" severity="warning" disabled={updatingViewIndex} onClick={() => navigate(`/manage-batches/${batchId}/attendance`)} />
                    </HasRequiredAuthority>,
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.CREATE_GLOBAL_NOTE}>
                        <Button
                            icon="pi pi-file-edit"
                            severity="secondary"
                            disabled={updatingViewIndex}
                            onClick={() =>
                                setDialogBulkGlobalNote({
                                    visible: true,
                                    batchId,
                                    students,
                                    closeDialog: closeDialogBulkGlobalNote,
                                })
                            }
                        />
                    </HasRequiredAuthority>,
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.CREATE_COUNSELING_NOTE}>
                        <Button
                            icon="pi pi-comments"
                            severity="secondary"
                            disabled={updatingViewIndex}
                            onClick={() =>
                                setDialogBulkCounselingNote({
                                    visible: true,
                                    batchId,
                                    students,
                                    closeDialog: closeDialogBulkCounselingNote,
                                })
                            }
                        />
                    </HasRequiredAuthority>,
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.MANAGE_BATCH_CONTROLLER}>
                        <Button
                            icon="pi pi-shield"
                            severity="help"
                            disabled={updatingViewIndex}
                            onClick={() =>
                                setDialogAddController({
                                    visible: true,
                                    batchId,
                                    closeDialog: closeDialogAddController,
                                })
                            }
                        />
                    </HasRequiredAuthority>,
                ]}
            />
            <Divider />

            {loading ? (
                <Loading />
            ) : error ? (
                <NoContent error={error} />
            ) : batch ? (
                <div className="flex-1 min-h-0 flex flex-column overflow-hidden">
                    <div className="px-3 pt-2 flex gap-4 flex-wrap">
                        <div>
                            <div className={`${TEXT_SMALL} text-color-secondary`}>Description</div>
                            <div className={TEXT_NORMAL}>{batch.description || "—"}</div>
                        </div>
                        <div>
                            <div className={`${TEXT_SMALL} text-color-secondary`}>Branch</div>
                            <div className={TEXT_NORMAL}>{batch.branch_title || "—"}</div>
                        </div>
                        <div>
                            <div className={`${TEXT_SMALL} text-color-secondary`}>Start</div>
                            <div className={TEXT_NORMAL}>{getReadableDate({ date: batch.start_date, removeTime: true })}</div>
                        </div>
                        <div>
                            <div className={`${TEXT_SMALL} text-color-secondary`}>End</div>
                            <div className={TEXT_NORMAL}>{getReadableDate({ date: batch.end_date, removeTime: true })}</div>
                        </div>
                    </div>

                    <TabView
                        className="flex-1 min-h-0 overflow-hidden"
                        activeIndex={activeTab}
                        onTabChange={(e) => setActiveTab(e.index)}
                        pt={{
                            root: { className: "flex flex-column h-full min-h-0 overflow-hidden" },
                            navContainer: { className: "px-2 flex-shrink-0" },
                            nav: { className: "flex-shrink-0" },
                            panelcontainer: { className: "flex-1 min-h-0 overflow-y-auto p-0" },
                            panelContainer: { className: "flex-1 min-h-0 overflow-y-auto p-0" },
                            panel: { className: "p-0" },
                        }}
                    >
                        <TabPanel header="Schedule">
                            <div className="p-3">
                                <BatchScheduleEditor batchId={batchId} />
                            </div>
                        </TabPanel>
                        <TabPanel header={`Students (${studentCount})`}>
                            <div className="p-3 flex flex-column gap-2">
                                <div className="flex align-items-center gap-2">
                                    {!!students?.length && (
                                        <InputText
                                            value={studentSearch}
                                            placeholder="Search students"
                                            className="flex-1"
                                            disabled={updatingViewIndex || savingRollNos}
                                            onChange={(e) => setStudentSearch(e.target.value)}
                                            pt={{ root: { className: TEXT_NORMAL } }}
                                        />
                                    )}
                                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.ASSIGN_BATCH_STUDENT}>
                                        {!!students?.length && (
                                            <Button
                                                loading={savingRollNos}
                                                onClick={() => {
                                                    showToast({
                                                        severity: "info",
                                                        summary: "Repositioning",
                                                        detail: `Repositioning Mode ${!updatingViewIndex ? "Enabled" : "Disabled"}`,
                                                        life: 1000,
                                                    });
                                                    if (!!updatingViewIndex) {
                                                        updateRollNos();
                                                    } else {
                                                        setStudentSearch("");
                                                    }
                                                    setUpdatingViewIndex((prev) => !prev);
                                                }}
                                                icon="pi pi-arrows-v"
                                            />
                                        )}
                                    </HasRequiredAuthority>
                                </div>

                                <OrderManager
                                    loading={studentsLoading}
                                    error={studentsError}
                                    entity="Students"
                                    items={filteredStudents}
                                    setItems={setStudents}
                                    updatingViewIndex={updatingViewIndex}
                                    itemTemplate={(item) => (
                                        <BatchStudent {...item} batchId={batchId} setStudents={setStudents} updatingViewIndex={updatingViewIndex} />
                                    )}
                                />
                            </div>
                        </TabPanel>
                    </TabView>
                </div>
            ) : (
                <NoContent />
            )}

            {dialogAddStudent?.visible && <DialogAddStudentToBatch {...dialogAddStudent} />}
            {dialogAddController?.visible && <DialogAddBatchController {...dialogAddController} />}
            {dialogBulkGlobalNote?.visible && <DialogBulkGlobalNote {...dialogBulkGlobalNote} />}
            {dialogBulkCounselingNote?.visible && <DialogBulkCounselingNote {...dialogBulkCounselingNote} />}
        </div>
    );
}
